import { css } from '@emotion/react';
import { theme } from '../../../design-system/theme/theme';
import type { ScheduleSlot } from '../../types';

export const titleCaptionStyle = css`
  font-size: ${theme.fontSize.sm}px;
  font-weight: 400;
  color: ${theme.color.textMuted};
`;

/* 날짜 이동 컨트롤 (◂ 날짜 ▸ · 오늘). 헤더 우측 legend 왼쪽에 배치. */
export const dateNavStyle = css`
  display: flex;
  align-items: center;
  gap: ${theme.space.xs}px;
`;

/* ◂ / ▸ 화살표 버튼 — 작고 정사각. */
export const navBtnStyle = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 1px solid ${theme.color.border};
  border-radius: ${theme.radius.sm}px;
  background: ${theme.color.surface};
  color: ${theme.color.text};
  font-size: ${theme.fontSize.md}px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease;

  &:hover:not(:disabled) {
    background: ${theme.color.primarySoft};
    border-color: ${theme.color.primary}80;
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

/* 선택 날짜 라벨. */
export const navDateStyle = css`
  min-width: 5.5rem;
  text-align: center;
  font-size: ${theme.fontSize.sm}px;
  font-weight: 600;
  color: ${theme.color.text};
`;

/*
 * "Today" 되돌리기 버튼 — 네비게이션 왼쪽 고정.
 * 오늘일 땐 visibility로 숨기되 공간은 유지해 ◂ 날짜 ▸ 가 밀리지 않게 한다.
 */
export const todayBtnStyle = (hidden: boolean) => css`
  height: 24px;
  padding: 0 ${theme.space.sm}px;
  border: 1px solid ${theme.color.primary}80;
  border-radius: ${theme.radius.sm}px;
  background: ${theme.color.primarySoft};
  color: ${theme.color.primary};
  font-size: ${theme.fontSize.sm}px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s ease;
  ${hidden && 'visibility: hidden; pointer-events: none;'}

  &:hover {
    background: ${theme.color.primary}26;
  }
`;

/* 확대 컨트롤 묶음 (돋보기 − 슬라이더 + ). 헤더 우측 date-nav 왼쪽. YouTube 스튜디오식. */
export const zoomCtrlStyle = css`
  display: flex;
  align-items: center;
  gap: ${theme.space.xs}px;
  color: ${theme.color.textMuted};
`;

/* 확대 정도 슬라이더. 왼쪽=전체(0~24), 오른쪽=최대 확대. */
export const zoomSliderStyle = css`
  width: 96px;
  height: 4px;
  cursor: pointer;
  accent-color: ${theme.color.primary};
`;

/* 모듈명 클릭 가능 — 모듈 관리로 이동. hover 시 primary 색 + 밑줄로 링크임을 표시. */
export const nameColClickableStyle = css`
  cursor: pointer;
  &:hover {
    color: ${theme.color.primary};
    text-decoration: underline;
  }
`;

export const axisRowStyle = css`
  display: flex;
  margin-bottom: ${theme.space.xs}px;
`;

/* 모듈명 열 8rem — 시안 w-32 스냅. */
export const nameColStyle = css`
  flex: 0 0 8rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${theme.fontSize.md}px;
  color: ${theme.color.text};
`;

export const axisStyle = css`
  position: relative;
  flex: 1;
  height: ${theme.space.md}px;
  font-size: ${theme.fontSize.sm}px;
  color: ${theme.color.textMuted};

  span {
    position: absolute;
    transform: translateX(-50%);
  }
`;

export const rowStyle = css`
  display: flex;
  align-items: center;
  padding: ${theme.space.xs}px 0;
`;

/* 그룹 구분 — 실행 이력 있는 모듈 ↔ 나머지 모듈 사이 여백 + 옅은 구분선. */
export const groupBreakStyle = css`
  margin-top: ${theme.space.md}px;
  border-top: 1px solid ${theme.color.border};
  padding-top: ${theme.space.sm}px;
`;

/* 트랙 높이 24px 고정. 확대 시 축 밖 막대가 넘치지 않게 overflow hidden. */
export const trackStyle = css`
  position: relative;
  flex: 1;
  height: 24px;
  border: 1px solid ${theme.color.border};
  border-radius: ${theme.radius.sm}px;
  background: ${theme.color.bg};
  overflow: hidden;
  /* 드래그로 시간 구간을 선택해 확대 — 선택 중 텍스트 드래그 방지. */
  cursor: crosshair;
  user-select: none;
`;

/* 드래그 확대 중 선택 구간을 트랙마다 표시(모든 행에 걸쳐 세로 밴드처럼 보임). */
export const selectionBandStyle = css`
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 0;
  background: ${theme.color.primary}26;
  border-left: 1px dashed ${theme.color.primary};
  border-right: 1px dashed ${theme.color.primary};
  pointer-events: none;
`;

export const gridlineStyle = css`
  position: absolute;
  top: 0;
  bottom: 0;
  border-left: 1px solid ${theme.color.border}80;
`;

export const nowLineStyle = css`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  z-index: 1;
  background: ${theme.color.primary}B3;
`;

const slotColor: Record<ScheduleSlot['state'], string> = {
  S: theme.color.success,
  Y: theme.color.success2, // 재시도 성공 — success 계열 밝은 톤
  F: theme.color.danger,
  R: theme.color.running,
  U: theme.color.surface,
};

/*
 * 슬롯. widthPct가 주어지면 duration 막대(24시간 대비 %), 없으면 기존 점(10px).
 * 실제 모듈은 실행이 수 분이라 24시간 축에서 폭이 0.x%라 거의 안 보인다.
 * 아주 짧은 실행도 눈에 띄고 클릭 가능하도록 min-width 10px 보장.
 */
export const slotStyle = (
  state: ScheduleSlot['state'],
  widthPct?: number,
  clickable?: boolean,
  manual?: boolean
) => css`
  position: absolute;
  top: 3px;
  height: 16px;
  border-radius: ${theme.radius.sm}px;
  background: ${slotColor[state]};
  ${state === 'U' && `border: 1px solid ${theme.color.border};`}
  ${widthPct !== undefined
    ? `width: ${widthPct}%; min-width: 10px;`
    : `width: 10px;`}
  ${clickable && 'cursor: pointer;'}
  /* 수동 실행 — 막대 안 'M' 글자 + 바깥 대비 링(box-shadow)으로 이중 표식. 상태색은 유지. */
  ${
    manual &&
    `z-index: 2;
     box-shadow: 0 0 0 1px ${theme.color.bg}, 0 0 0 2px ${theme.color.secondary};
     &::after {
       content: 'M';
       position: absolute;
       inset: 0;
       display: flex;
       align-items: center;
       justify-content: center;
       font-size: 9px;
       font-weight: 500;
       line-height: 1;
       color: #fff;
     }`
  }
`;
